/**
 * Turning an `ApiFailure` into something a screen can render.
 *
 * Design doc §22.4 asks for "retriable vs non-retriable error language", and
 * singles out the unknown publication state as a case where retry must be
 * actively discouraged. Both decisions are made once, here, so no screen gets
 * to invent its own answer.
 */
import { toFailure, type ApiFailure } from '@cedarreachai/api-client';

export type FailureKind =
  /** Transient. Offering "Try again" is correct. */
  | 'retriable'
  /** Needs a human to resolve state first. Offer the resolving action, not retry. */
  | 'terminal'
  /**
   * The external outcome is genuinely unknown. Retry is *unsafe*, not merely
   * useless: it may publish a second time. The UI must say so (§22.4).
   */
  | 'unknown-outcome';

export interface PresentableFailure {
  readonly kind: FailureKind;
  /** i18n key under `errors.*` describing what to do next. */
  readonly guidanceKey: string;
  /** Stable machine code, shown to the user so a support request is traceable. */
  readonly code: string;
  /** Server-authored message. Never translated — it may name specific data. */
  readonly detail: string;
  readonly requestId: string | undefined;
  readonly failure: ApiFailure;
}

const TERMINAL_GUIDANCE: Readonly<Record<string, string>> = {
  APPROVAL_INVALIDATED: 'errors.guidance.approvalInvalidated',
  UNSUPPORTED_CLAIM: 'errors.guidance.unsupportedClaim',
  INSUFFICIENT_EVIDENCE: 'errors.guidance.unsupportedClaim',
  WORKSPACE_PAUSED: 'errors.guidance.workspacePaused',
  AUTOMATION_LEVEL_TOO_LOW: 'errors.guidance.automationLevelTooLow',
  BUDGET_EXCEEDED: 'errors.guidance.budgetExceeded',
  FORBIDDEN: 'errors.guidance.forbidden',
  UNAUTHENTICATED: 'errors.guidance.unauthenticated',
  REAUTHENTICATION_REQUIRED: 'errors.guidance.reauthenticationRequired',
  CSRF_REJECTED: 'errors.guidance.csrfRejected',
  NOT_FOUND: 'errors.guidance.notFound',
  VERSION_CONFLICT: 'errors.guidance.versionConflict',
  CONFLICT: 'errors.guidance.versionConflict',
  IDEMPOTENCY_KEY_REUSED: 'errors.guidance.idempotencyKeyReused',
  VALIDATION_FAILED: 'errors.guidance.validationFailed',
  CONNECTOR_REAUTH_REQUIRED: 'errors.guidance.connectorReauthRequired',
};

/** Classifies a thrown error. Returns `null` when it is not an API failure at all. */
export function presentFailure(error: unknown): PresentableFailure | null {
  const failure = toFailure(error);
  if (!failure) return null;

  if (failure.kind === 'transport') {
    return {
      kind: 'retriable',
      guidanceKey: 'errors.guidance.transport',
      code: 'TRANSPORT',
      detail: failure.message,
      requestId: failure.requestId,
      failure,
    };
  }

  if (failure.kind === 'malformed') {
    return {
      kind: failure.retriable ? 'retriable' : 'terminal',
      guidanceKey: failure.retriable ? 'errors.guidance.transient' : 'errors.guidance.unexpected',
      code: `HTTP_${failure.status}`,
      detail: failure.message,
      requestId: failure.requestId,
      failure,
    };
  }

  // The first request with this key is still running on the server. Retrying
  // the same action (same key) is safe and is the right next step; the server
  // answers it with the first request's result once that finishes.
  if (failure.code === 'IDEMPOTENCY_REQUEST_IN_PROGRESS') {
    return {
      kind: 'retriable',
      guidanceKey: 'errors.guidance.idempotencyInProgress',
      code: failure.code,
      detail: failure.message,
      requestId: failure.requestId,
      failure,
    };
  }

  if (failure.code === 'PUBLICATION_STATE_UNKNOWN') {
    return {
      kind: 'unknown-outcome',
      guidanceKey: 'errors.guidance.publicationStateUnknown',
      code: failure.code,
      detail: failure.message,
      requestId: failure.requestId,
      failure,
    };
  }

  const guidanceKey =
    TERMINAL_GUIDANCE[failure.code] ??
    (failure.retriable ? 'errors.guidance.transient' : 'errors.guidance.unexpected');

  return {
    kind: failure.retriable ? 'retriable' : 'terminal',
    guidanceKey,
    code: failure.code,
    detail: failure.message,
    requestId: failure.requestId,
    failure,
  };
}

/**
 * The expected hash the server reported when it refused a decision, if any.
 *
 * `services/api/src/routes/approvals.rs` puts it in `details` on
 * `APPROVAL_INVALIDATED`; the reviewer must reload rather than resubmit,
 * because resubmitting would approve bytes they never read.
 */
export function expectedContentSha256(failure: ApiFailure): string | null {
  if (failure.kind !== 'api') return null;
  const value = (failure.details as Record<string, unknown>)['expected_content_sha256'];
  return typeof value === 'string' ? value : null;
}
